import React from 'react';
import {GatsbyImage as Img, getImage} from 'gatsby-plugin-image';
import ReactMarkdown from 'react-markdown';
import * as s from './case-studies.module.less';
import * as landingPageStyle from '../landing-page.module.less';
import Pane from '../../pane/pane';
import PaneTitle from '../../pane/pane-title/pane-title';
import cn from 'classnames';
import MockupIMac from "../../mockups/imac";
import MockupIPad from "../../mockups/ipad";
import MockupMacbook from "../../mockups/macbook";
import MockupIPhone from "../../mockups/iphone";

// Placeholder image function (replace with actual logic or remove if using direct paths)
const getPlaceholderImage = () => 'https://via.placeholder.com/600x400.png/f0f0f0/cccccc?text=Software+Screenshot';

// Placeholder for a component that can render Markdown, or use a library
// const MarkdownRenderer = ({ content }) => <div dangerouslySetInnerHTML={{ __html: content }} />; // Basic example, consider security for __html
// This might still be useful if other parts of the study object have markdown, but not for baseline/results points.

const CaseStudies = ({ heading, description, studies, backgroundColor }) => {
  if (!studies || studies.length === 0) {
    return null;
  }

  const getMockup = (type, screenShotImage, companyName) => {
    switch (type) {
      case "mobile":
        return (
            <MockupIPhone
                screenShot={getImage(screenShotImage)}
                companyName={companyName}
            />
        )
      case "tablet":
        return (
            <MockupIPad
                screenShot={getImage(screenShotImage)}
                companyName={companyName}
            />
        )
      case "laptop":
        return (
            <MockupMacbook
                screenShot={getImage(screenShotImage)}
                companyName={companyName}
            />
        )
      case "desktop":
      default:
        return (
            <MockupIMac
                screenShot={getImage(screenShotImage)}
                companyName={companyName}
            />
        )
    }
  }

  return (
    <Pane
      bgColor={backgroundColor}
    >
      <div className={landingPageStyle.container}>
        <PaneTitle title={heading} description={description} isCentered />
        <div className={s.caseStudiesContainer}>
          {studies.map((studyWrapper, index) => {
            const study = studyWrapper.node.frontmatter;
            const companyName = study.companyName;
            const baselinePoints = study.baselineSituationPoints || [];
            const resultsPoints = study.resultsPoints || [];
            const logoImage = study.imageDarkPath || study.imagePath;
            const screenShotImage = study.screenShot;
            const mockupType = study.mockupType || undefined

            return (
              <div key={index} className={s.caseStudyItem}>
                <div className={s.textualContentContainer}>
                  <div className={s.situationSection}>
                    <h4>Ausgangssituation</h4>
                    {baselinePoints.length > 0 ? (
                      <ul className={cn(s.pointsList, s.baselinePoints)}>
                        {baselinePoints.map((point, i) => (
                          <li key={i} className={s.pointItem}>
                            <span className={s.checkmark}>&#8226;</span> <ReactMarkdown>{point}</ReactMarkdown>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>Details zur Ausgangssituation nicht verfügbar.</p>
                    )}
                  </div>
                  <div className={s.resultsSection}>
                    <h4>Ergebnis nach Zusammenarbeit</h4>
                    {resultsPoints.length > 0 ? (
                      <ul className={cn(s.pointsList, s.resultsPoints)}>
                        {resultsPoints.map((point, i) => {
                          let itemClass = s.pointItem;
                          if (point.startsWith('+')) {
                            itemClass = cn(s.pointItem, s.pointPositive);
                          } else if (point.startsWith('-')) {
                            itemClass = cn(s.pointItem, s.pointNegative);
                          }
                          return (
                            <li key={i} className={itemClass}>
                              <span className={s.checkmark}>&#10003;</span> <ReactMarkdown>{point.substring(point.startsWith('+') || point.startsWith('-') ? 1 : 0).trim()}</ReactMarkdown>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p>Details zu den Ergebnissen nicht verfügbar.</p>
                    )}
                  </div>
                </div>

                <div className={s.mediaAndDetailsContainer}>
                  <div className={s.mediaPlaceholder}>
                    {screenShotImage ? (
                       getMockup(mockupType, screenShotImage, companyName)
                    ) : (
						<div className={s.videoPlaceholder}>Video/Image Placeholder</div>
					)}
                  </div>
                  <div className={s.companyInfo}>
                    <h3>{companyName}</h3>
                    {logoImage && (
                      logoImage.childImageSharp ? (
                        <Img
                          image={getImage(logoImage)}
                          alt={`${companyName} logo`}
                          className={s.companyLogoSmall}
                        />
                      ) : (
                        <img
                          src={logoImage.publicURL}
                          alt={`${companyName} logo`}
                          className={s.companyLogoSmall}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Pane>
  );
};

export default CaseStudies;
